/**
 * Pure rules of the dealer workspace: who gets it, its navigation and route guard, and the words and numbers its
 * screens show (the call drawer's transcript, where a request stands: awaiting the store's confirmation, confirmed by
 * a named advisor, arrived; the CRM hand-off, the sales pipeline, store hours, the price book). The store's own staff
 * confirm requests and mark arrivals; nobody on our side does, and no label here may say so. No React, no imports but
 * types, so node runs it directly.
 *   cd app && npm test        (scripts/test.mjs strips the types and runs test/*.test.mjs with node:test)
 */
import type { AppointmentStatus, CallEntry, PipelineEntry, PriceBookItem, SalesStage, WeekHours } from '../calls/provider';

// ── who gets the dealer workspace ───────────────────────────────────────────────────────────────────────────────────
/** The switches (shared/flags.mjs): the workspace needs its own and the call log's. */
export const DEALER_FLAG = 'dealer_workspace';
export const CALLS_FLAG = 'store_desk';
export interface WorkspaceFacts {
  segment: string | null;
  flags: Record<string, boolean>;
}
/** A dealership store with both switches on gets the dealer workspace; every other store keeps the workshop app. */
export const isDealerWorkspace = (t: WorkspaceFacts) => t.segment === 'dealership' && !!t.flags[DEALER_FLAG] && !!t.flags[CALLS_FLAG];
/** "Harborview Motors (sample)" → "Harborview Motors": a bracketed note is never part of what the store is called. */
export const storeDisplayName = (name: string) =>
  name
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || name;
/** A sample store (tenant settings.sample) shows one discreet badge, and nothing else says so. */
export const isSampleStore = (settings: Record<string, unknown> | null | undefined) => settings?.sample === true;
/** "Harborview Motors" → "HM" */
export const initialsOf = (name: string) =>
  storeDisplayName(name)
    .split(/\s+/)
    .filter((w) => !/^(llc|inc|co|the|&|and)$/i.test(w))
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

// ── navigation ──────────────────────────────────────────────────────────────────────────────────────────────────────
export type DealerIcon =
  'gauge' | 'phone' | 'calendar' | 'chart' | 'users' | 'book' | 'store' | 'check-circle' | 'building' | 'inbox' | 'mail' | 'van';
export interface DealerNavItem {
  path: string;
  label: string;
  icon: DealerIcon;
  perm: string[];
  phone: boolean;
  /** Shown only while its switch is on: the CRM screens (dealer_crm), dealer groups (store_groups), texting (texting),
   * campaigns (campaigns) or the inventory feed (inventory_feed). */
  needs?: 'crm' | 'groups' | 'texting' | 'campaigns' | 'inventory';
}
/** The dealer's sections, in order. `perm` is any-of (the server enforces each route's own permission). */
export const DEALER_NAV: readonly DealerNavItem[] = [
  { path: '/home', label: 'Home', icon: 'gauge', perm: ['analytics.view', 'leads.view'], phone: true },
  { path: '/calls', label: 'Calls', icon: 'phone', perm: ['leads.view'], phone: true },
  { path: '/appointments', label: 'Appointments', icon: 'calendar', perm: ['leads.view'], phone: true },
  { path: '/sales', label: 'Sales', icon: 'chart', perm: ['leads.view'], phone: true },
  // The store's listed vehicles (switch inventory_feed; inventoryModel.ts inventoryOn); on a phone under More.
  { path: '/inventory', label: 'Inventory', icon: 'van', perm: ['vehicle_inventory.view'], phone: false, needs: 'inventory' },
  { path: '/customers', label: 'Customers', icon: 'users', perm: ['leads.view', 'customers.view'], phone: false },
  { path: '/texts', label: 'Texts', icon: 'inbox', perm: ['leads.view'], phone: false, needs: 'texting' },
  { path: '/tasks', label: 'Tasks', icon: 'check-circle', perm: ['tasks.work', 'tasks.assign'], phone: false, needs: 'crm' },
  { path: '/campaigns', label: 'Campaigns', icon: 'mail', perm: ['campaigns.view'], phone: false, needs: 'campaigns' },
  { path: '/reports', label: 'Reports', icon: 'book', perm: ['analytics.view'], phone: false },
  { path: '/group', label: 'Group', icon: 'building', perm: ['group.view'], phone: false, needs: 'groups' },
  { path: '/store', label: 'Store setup', icon: 'store', perm: ['settings.manage', 'leads.view', 'analytics.view'], phone: false },
];
/** The sections a person sees: their permissions, and the CRM (or group) sections only while those switches are on. */
export const visibleDealerNav = (
  can: (perm: string[]) => boolean,
  on: { crm?: boolean; groups?: boolean; texting?: boolean; campaigns?: boolean; inventory?: boolean } = {},
) => DEALER_NAV.filter((n) => can(n.perm) && (!n.needs || on[n.needs] === true));
/** Paths of the dealer workspace beyond its sections (the account's own security screen). */
const OTHER = ['/account/security'];
/**
 * Where a dealer is sent for a path: null when the path is a dealer screen; otherwise its first section. Every
 * workshop module (/today, /jobs, /dispatch, /payments, /settings, /leads, …) is outside the workspace and redirects.
 */
export function dealerRedirect(pathname: string, items: readonly DealerNavItem[]): string | null {
  const home = items[0]?.path ?? '/home';
  const p = pathname.replace(/\/+$/, '') || '/';
  if (OTHER.includes(p)) return null;
  return items.some((n) => p === n.path || p.startsWith(`${n.path}/`)) ? null : home;
}
/** The section a path belongs to ('/calls/abc' → '/calls'). */
export const sectionOf = (pathname: string) => `/${pathname.split('/')[1] ?? ''}`;

// ── time words ──────────────────────────────────────────────────────────────────────────────────────────────────────
/** Seconds → "0:42", "12:05" */
export const clock = (sec: number) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
/** "Good morning" / "Good afternoon" / "Good evening" for a store-local hour (0–23). */
export const greeting = (hour: number) => (hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening');
/**
 * A report email's first line, as the advisor's own copy reads it (desk_log/emails.js greetingLine): "Good morning,
 * Morgan," for "Morgan Ellis"; "Good morning." when no one is named.
 */
export function mailGreeting(name: string | null | undefined): string {
  const first = String(name ?? '')
    .trim()
    .split(/\s+/)[0];
  return first ? `Good morning, ${first},` : 'Good morning.';
}
/** 6.5 → "6.5 min", 7 → "7 min", null → "—" */
export const minutesShort = (m: number | null | undefined) => (m == null ? '—' : `${Number.isInteger(m) ? m : m.toFixed(1)} min`);

// ── the call drawer ─────────────────────────────────────────────────────────────────────────────────────────────────
const fmtTimeIn = (iso: string, tz: string) =>
  new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
export interface Bubble {
  who: 'caller' | 'assistant';
  text: string;
  offset: string;
  sec: number;
}
/**
 * The transcript as chat bubbles, in order: each turn's caller line, then the assistant's. `offset` is the time into
 * the call ("0:42"), from the turn's own time and the moment the call was answered; a turn without a time takes the
 * previous one's. Empty lines are dropped.
 */
export function transcriptBubbles(
  turns: readonly { turn_no: number; asr_text: string | null; bot_text: string | null; at: string | null }[],
  answeredAt: string | null,
): Bubble[] {
  const t0 = answeredAt ? Date.parse(answeredAt) : NaN;
  let last = 0;
  const out: Bubble[] = [];
  for (const t of [...turns].sort((a, b) => a.turn_no - b.turn_no)) {
    const at = t.at ? Date.parse(t.at) : NaN;
    const sec = Number.isFinite(at) && Number.isFinite(t0) ? Math.max(last, Math.round((at - t0) / 1000)) : last;
    last = sec;
    if (t.asr_text?.trim()) out.push({ who: 'caller', text: t.asr_text.trim(), offset: clock(sec), sec });
    if (t.bot_text?.trim())
      out.push({
        who: 'assistant',
        text: t.bot_text.trim(),
        offset: clock(sec + (t.asr_text?.trim() ? 2 : 0)),
        sec: sec + (t.asr_text?.trim() ? 2 : 0),
      });
  }
  return out;
}

/** Whole minutes from the call to the store's confirmation; null when either is missing or out of order. */
export function confirmMinutes(e: Pick<CallEntry, 'answered_at' | 'appointment'>): number | null {
  if (!e.appointment.confirmed_at) return null;
  const ms = Date.parse(e.appointment.confirmed_at) - Date.parse(e.answered_at);
  return Number.isFinite(ms) && ms >= 0 ? Math.max(1, Math.round(ms / 60_000)) : null;
}
/**
 * How the store's confirmation reads beside an appointment: "12 min after the call" when it came within the hour;
 * otherwise when it was confirmed ("Mon, Oct 19, 7:12 AM"), so a request taken while the store was closed never reads
 * as a slow "11 h after the call". null when the request is not confirmed.
 */
export function confirmedNote(
  e: Pick<CallEntry, 'answered_at' | 'appointment'>,
  tz: string,
): { soon: true; text: string } | { soon: false; text: string } | null {
  const m = confirmMinutes(e);
  if (m == null || !e.appointment.confirmed_at) return null;
  if (m < 60) return { soon: true, text: `${m} min after the call` };
  const at = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return { soon: false, text: at.format(new Date(e.appointment.confirmed_at)) };
}
/** "7 min" / "2 h 5 min" / "3 days": how long after the call the store confirmed. */
export function sinceCall(minutes: number | null): string | null {
  if (minutes == null) return null;
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60} min` : ''}`;
  const d = Math.round(minutes / (24 * 60));
  return `${d} ${d === 1 ? 'day' : 'days'}`;
}
/** A service or test-drive request the store has not confirmed yet. */
export const isAwaitingConfirmation = (e: Pick<CallEntry, 'outcome' | 'kind'>) =>
  e.outcome === 'request_left' && (e.kind === 'service_appointment' || e.kind === 'sales_appointment');
export type RequestState = 'awaiting' | 'confirmed' | 'booked';
/**
 * Where an appointment request stands, in the store's own words: "Awaiting your confirmation" until the store's
 * staff confirm it; "Confirmed by Morgan E." once a named member of the store's staff has; "In your scheduler" for a
 * booking that carries no name (entered another way). null when the call is not an appointment request.
 */
export function requestLine(e: Pick<CallEntry, 'outcome' | 'kind' | 'confirmed_by_name'>): { text: string; state: RequestState } | null {
  if (isAwaitingConfirmation(e)) return { text: 'Awaiting your confirmation', state: 'awaiting' };
  if (e.outcome !== 'booked') return null;
  if (e.confirmed_by_name) return { text: `Confirmed by ${e.confirmed_by_name}`, state: 'confirmed' };
  return { text: 'In your scheduler', state: 'booked' };
}
/** The outcome as a dealer reads it: an appointment request awaiting the store is "Appointment requested", not a call back. */
export function outcomeLabel(e: Pick<CallEntry, 'outcome' | 'kind'>): string {
  if (isAwaitingConfirmation(e)) return 'Appointment requested';
  return OUTCOME_WORDS[e.outcome] ?? e.outcome;
}
/** The outcomes as the dealer's filter names them (a booking is "Confirmed": the store's own staff confirmed it). */
export const DEALER_OUTCOME_LABEL: Record<string, string> = {
  booked: 'Confirmed',
  request_left: 'Call back requested',
  message: 'Message taken',
  transferred: 'Transferred',
  hung_up: 'Caller hung up',
  wrong_number: 'Wrong number',
};
const OUTCOME_WORDS = DEALER_OUTCOME_LABEL;
/** "Sent to your advisors 7:14 PM": when the request reached the store (the entry is logged as the call ends). */
export const sentToAdvisors = (e: Pick<CallEntry, 'created_at'>, tz: string) => `Sent to your advisors ${fmtTimeIn(e.created_at, tz)}`;
/** The time the caller asked for: a test drive's time, or the words the caller used. */
export function requestedTime(e: Pick<CallEntry, 'kind' | 'preferred_time' | 'interest'>, tz: string): string | null {
  if (e.kind === 'sales_appointment' && e.interest.test_drive_at)
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(e.interest.test_drive_at));
  return e.preferred_time;
}

/** "2025 Toyota RAV4 Hybrid XLE" from a year, make and model (blank parts left out). */
const carName = (v: { year: number | null; make: string | null; model: string | null }) =>
  [v.year, v.make, v.model].filter((x) => x != null && x !== '').join(' ');
/**
 * The vehicle a call is about, as a list line reads it. A sales call is about the vehicle the caller asked about (the
 * car they drive, often a trade-in, is `own`); any other call is about the caller's own vehicle. Empty strings when
 * not given.
 */
export function callVehicle(e: Pick<CallEntry, 'department' | 'kind' | 'vehicle' | 'interest'>): { main: string; own: string } {
  const own = carName(e.vehicle);
  const want = carName(e.interest);
  if ((e.department === 'sales' || e.kind === 'sales_appointment') && want) return { main: want, own };
  return { main: own || want, own: '' };
}

// ── the store confirms a request (the call sheet's form) ───────────────────────────────────────────────────────────
const wallMs = (ms: number, tz: string) => {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
      .formatToParts(new Date(ms))
      .map((x) => [x.type, x.value]),
  );
  return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour) % 24, Number(p.minute));
};
/** 'YYYY-MM-DD' + 'HH:MM' in the store's zone → the instant, as ISO; null for anything that is not a date and a time. */
export function zonedIso(date: string, time: string, tz: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m || !t) return null;
  const guess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(t[1]), Number(t[2]));
  let utc = guess - (wallMs(guess, tz) - guess);
  utc = guess - (wallMs(utc, tz) - utc); // once more for a guess that crossed a clock change
  return Number.isFinite(utc) ? new Date(utc).toISOString() : null;
}
/** What the confirm form refuses, by field. The reference is the number the store's own scheduler gave the booking. */
export function confirmErrors(f: { ref: string; date: string; time: string }, tz: string): Record<string, string> {
  const out: Record<string, string> = {};
  const ref = f.ref.trim();
  if (!ref) out.ref = 'The reference from your scheduler';
  else if (ref.length > 64) out.ref = 'At most 64 characters';
  if (!zonedIso(f.date, f.time, tz)) out.at = 'The confirmed date and time';
  return out;
}

/**
 * How the request reached the advisors, in words, for the call's timeline ("Sent to your advisors"). Every request is in
 * this app the moment the call ends and in the advisors' morning summary email; no email goes to an advisor per request.
 * A sales lead also goes to the store's CRM as an ADF lead email, when one was queued or sent.
 */
export function deliveredLine(e: Pick<CallEntry, 'lead_email'>): string {
  const crm = e.lead_email?.status === 'sent' || e.lead_email?.status === 'queued';
  return crm ? 'In this app and the morning summary, and to your CRM as an ADF lead' : 'In this app and the morning summary';
}

/** The CRM hand-off of a sales call, in words: { text, tone }. null when the call is not one that goes to the CRM. */
export function crmLine(
  e: Pick<CallEntry, 'department' | 'kind' | 'lead_email'>,
  tz: string,
): { text: string; tone: 'done' | 'wait' | 'off' } | null {
  if (e.department !== 'sales' && e.kind !== 'lead' && e.kind !== 'sales_appointment') return null;
  const le = e.lead_email;
  if (!le) return { text: 'Not sent to your CRM', tone: 'off' };
  if (le.status === 'sent')
    return { text: `Sent to your CRM${le.sent_at ? ` at ${fmtTimeIn(le.sent_at, tz)}` : ''}${le.to ? ` · ${le.to}` : ''}`, tone: 'done' };
  if (le.status === 'queued') return { text: 'Sending to your CRM', tone: 'wait' };
  if (le.status === 'failed') return { text: 'Could not be sent to your CRM', tone: 'off' };
  return { text: 'Not sent to your CRM (no lead inbox set)', tone: 'off' };
}

// ── appointments ────────────────────────────────────────────────────────────────────────────────────────────────────
export const APPOINTMENT_STATUS: Record<AppointmentStatus, { label: string; tone: 'info' | 'wait' | 'done' | 'bad' }> = {
  upcoming: { label: 'Confirmed', tone: 'info' },
  awaiting_arrival: { label: 'Awaiting check-in', tone: 'wait' },
  arrived: { label: 'Arrived', tone: 'done' },
  no_show: { label: 'No-show', tone: 'bad' },
};
/** Group by store-local day, in order: [{ date, items }] */
export function byDay<T>(items: readonly T[], at: (x: T) => string, tz: string): { date: string; items: T[] }[] {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
  const out: { date: string; items: T[] }[] = [];
  for (const x of items) {
    const d = fmt.format(new Date(at(x)));
    const last = out[out.length - 1];
    if (last && last.date === d) last.items.push(x);
    else out.push({ date: d, items: [x] });
  }
  return out;
}
/** 'YYYY-MM-DD' against today → "Today", "Tomorrow", "Yesterday", else "Tuesday, Oct 20" */
export function dayLabel(date: string, today: string): string {
  const diff = Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric' }).format(
    new Date(`${date}T12:00:00Z`),
  );
}

// ── the sales pipeline ──────────────────────────────────────────────────────────────────────────────────────────────
export const STAGES: readonly { key: SalesStage; label: string; hint: string }[] = [
  { key: 'new', label: 'New', hint: 'Enquiries waiting for a call back' },
  { key: 'contacted', label: 'Contacted', hint: 'Your team has followed up' },
  { key: 'test_drive_booked', label: 'Test drive confirmed', hint: "In your sales team's schedule" },
  { key: 'showed', label: 'Showed', hint: 'Came in for the test drive' },
];
const RANK: Record<SalesStage, number> = { new: 0, contacted: 1, test_drive_booked: 2, showed: 3 };
/**
 * One card per prospect (by phone number; a call without a number is its own card), at the most advanced stage any of
 * their calls reached; newest activity first in each column. `calls` is how many sales calls the card stands for.
 */
export function pipelineColumns(items: readonly PipelineEntry[]): Record<SalesStage, (PipelineEntry & { calls: number })[]> {
  const best = new Map<string, PipelineEntry & { calls: number }>();
  for (const e of items) {
    const k = e.caller.phone ?? `id:${e.id}`;
    const had = best.get(k);
    if (!had) {
      best.set(k, { ...e, calls: 1 });
      continue;
    }
    const pick = RANK[e.stage] > RANK[had.stage] || (RANK[e.stage] === RANK[had.stage] && e.answered_at > had.answered_at) ? e : had;
    best.set(k, { ...pick, calls: had.calls + 1 });
  }
  const cols: Record<SalesStage, (PipelineEntry & { calls: number })[]> = { new: [], contacted: [], test_drive_booked: [], showed: [] };
  for (const c of best.values()) cols[c.stage].push(c);
  for (const k of Object.keys(cols) as SalesStage[]) cols[k].sort((a, b) => b.answered_at.localeCompare(a.answered_at));
  return cols;
}

// ── the store card, as a dealer reads it ────────────────────────────────────────────────────────────────────────────
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** '07:00' → "7 AM", '17:30' → "5:30 PM", '24:00' → "midnight" */
export function time12(hhmm: string): string {
  if (hhmm === '24:00') return 'midnight';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h! % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h! < 12 ? 'AM' : 'PM'}`;
}
/**
 * Weekly hours as rows a person reads, Monday first, consecutive days with the same hours joined:
 * [{ days: 'Mon – Fri', hours: '7 AM – 6 PM' }, { days: 'Sat', … }, { days: 'Sun', hours: 'Closed' }]
 */
export function hoursRows(h: WeekHours): { days: string; hours: string }[] {
  const order = [1, 2, 3, 4, 5, 6, 0];
  const text = (d: number) => {
    const w = h[String(d) as keyof WeekHours];
    return w && w.length ? w.map((x) => `${time12(x.start)} – ${time12(x.end)}`).join(', ') : 'Closed';
  };
  const rows: { from: number; to: number; hours: string }[] = [];
  for (const d of order) {
    const t = text(d);
    const last = rows[rows.length - 1];
    if (last && last.hours === t) last.to = d;
    else rows.push({ from: d, to: d, hours: t });
  }
  return rows.map((r) => ({ days: r.from === r.to ? DAY_SHORT[r.from]! : `${DAY_SHORT[r.from]} – ${DAY_SHORT[r.to]}`, hours: r.hours }));
}
/** A price-book line's price for the screen: "$89.95", "No charge" or "Quoted at the store" (never "$0"). */
export function priceLabel(i: Pick<PriceBookItem, 'price_cents' | 'description'>): string {
  if (i.price_cents > 0)
    return `$${(i.price_cents / 100).toLocaleString('en-US', { minimumFractionDigits: i.price_cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
  return /no charge|complimentary|free/i.test(i.description ?? '') ? 'No charge' : 'Quoted at the store';
}

// ── the home screen ─────────────────────────────────────────────────────────────────────────────────────────────────
/** Calls per store-local day for the last `days` days ending `today` (oldest first), from a list of entries. */
/**
 * A bar's height in pixels on the calls-per-day chart: in proportion to the busiest day (which fills `full`), never
 * under 3 px so an empty day still shows. Pixels, not a percentage: a percentage height inside an auto-sized grid row
 * resolves to nothing and every bar renders flat.
 */
export function barHeight(calls: number, max: number, full: number): number {
  if (!(max > 0) || !(calls > 0)) return 3;
  return Math.max(3, Math.round((Math.min(calls, max) / max) * full));
}
export function dailyCounts(
  entries: readonly Pick<CallEntry, 'answered_at' | 'source'>[],
  tz: string,
  today: string,
  days: number,
): { date: string; calls: number }[] {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
  const n = new Map<string, number>();
  for (const e of entries)
    if (e.source !== 'web') {
      const d = fmt.format(new Date(e.answered_at));
      n.set(d, (n.get(d) ?? 0) + 1);
    }
  const out: { date: string; calls: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.parse(`${today}T12:00:00Z`) - i * 86_400_000).toISOString().slice(0, 10);
    out.push({ date: d, calls: n.get(d) ?? 0 });
  }
  return out;
}
/** Change against the period before, as a short badge: { text: '+12%', dir } ; null when there is nothing to compare. */
export function trend(cur: number, prev: number): { text: string; dir: 'up' | 'down' | 'flat' } | null {
  if (prev <= 0) return cur > 0 ? { text: 'New', dir: 'up' } : null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { text: `${pct > 0 ? '+' : ''}${pct}%`, dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}
/** 315000 → "$3,150" */
export const usd = (cents: number) => `$${Math.round(cents / 100).toLocaleString('en-US')}`;

// ── getting live (the set-up checklist, GET /v1/app/onboarding) ──────────────────────────────────────────────────────
export interface SetupStep {
  key: string;
  title: string;
  status: 'todo' | 'in_progress' | 'done' | 'skipped';
}
/** What each dealership step asks of the store, and who does it (shared/onboarding.mjs DEALERSHIP_ONBOARDING_STEPS). */
export const SETUP_HINTS: Record<string, string> = {
  account: 'Sign in once with the invitation we emailed you.',
  store_card: 'Add your service hours in Store setup.',
  phone_number: 'We set up your service and sales lines.',
  call_forwarding: 'Once your test calls are approved, forward your lines to ours; we confirm it works.',
  scheduler: 'Add the link to your online scheduler in Store setup.',
  adf_inbox: 'Add your CRM lead inbox for sales calls.',
  advisors: 'Choose who gets the morning summary and the Monday report.',
  test_call: 'Before anything is forwarded, you hear test calls to your own assistant and approve them; we then mark this step.',
};
/**
 * The "Getting live" card: shown while the store is onboarding and any step but go-live is open; `next` is the first
 * open step. Steps keep the order the server sent; go-live itself is not listed (it follows the others).
 */
export function gettingLive(tenantStatus: string, steps: readonly SetupStep[]) {
  const open = (s: SetupStep) => s.status !== 'done' && s.status !== 'skipped';
  const list = steps.filter((s) => s.key !== 'go_live');
  return {
    show: tenantStatus === 'onboarding' && list.some(open),
    done: list.filter((s) => !open(s)).length,
    total: list.length,
    next: list.find(open)?.key ?? null,
    steps: list.map((s) => ({ key: s.key, title: s.title, open: open(s), hint: SETUP_HINTS[s.key] ?? '' })),
  };
}
